import { ApiProperty } from '@nestjs/swagger';
import {
  ILiquidacionAgencia,
  ILiquidacionAgenciaPdfData,
} from '../interfaces/liquidacion-agencia.interface';

/**
 * DTO simplificado para una liquidación de agencia individual.
 * Usado como elemento del array en LiquidacionAgenciaResponseDto.
 */
export class LiquidacionAgenciaItemDto {
  @ApiProperty({ example: 422 })
  id!: number;

  @ApiProperty({ example: '000000422' })
  numeroLiquidacion!: string;

  @ApiProperty()
  fromDate!: Date;

  @ApiProperty()
  toDate!: Date;

  @ApiProperty({ example: 2 })
  statusId!: number;

  @ApiProperty({ example: 66 })
  customerId!: number;

  @ApiProperty({ required: false })
  totalSaleAmount?: number;

  @ApiProperty({ required: false })
  totalTransferAmount?: number;

  @ApiProperty({ required: false })
  totalInvoiceAmount?: number;

  @ApiProperty({ example: 15 })
  cntr!: number;

  @ApiProperty({ example: 450 })
  plts!: number;

  @ApiProperty({ required: false, example: 'MSC SANTA MARIA' })
  nave?: string;

  @ApiProperty({
    required: false,
    example: 'TERRESTRE 47, TERRESTRE 50',
    description: 'Lista de nombres de naves para tooltip (separados por coma)',
  })
  navesTooltip?: string;

  @ApiProperty({ example: 273000 })
  agenciamiento!: number;

  @ApiProperty({ example: 0 })
  transporte!: number;

  @ApiProperty({ example: 0 })
  osVenta!: number;

  @ApiProperty({
    example: 273000,
    description: 'Total Venta Neto (Agenciamiento + Transporte + OS Venta)',
  })
  totalVentaNeto!: number;

  @ApiProperty({ example: 0 })
  osTraspaso!: number;

  @ApiProperty({ example: 273000 })
  total!: number;

  @ApiProperty({ required: false, example: 'May 2025' })
  mesAnio?: string;

  @ApiProperty({ required: false, example: 'Liquidado' })
  estado?: string;

  @ApiProperty({ required: false, example: 'ACOGRAPES' })
  cliente?: string;

  @ApiProperty({ required: false, example: 'Liquidación' })
  tipo?: string;
}

/**
 * DTO para la respuesta paginada de liquidaciones de agencia.
 * Incluye la lista de liquidaciones de agencia y metadatos de paginación.
 *
 * Uso: Respuesta del endpoint GET /api/v1/liquidaciones-agencia.
 */
export class LiquidacionAgenciaResponseDto {
  /**
   * Lista de liquidaciones de agencia para la página solicitada.
   */
  @ApiProperty({
    description: 'Lista de liquidaciones de agencia para la página actual',
    type: 'array',
    items: { type: 'object' },
    example: [
      {
        id: 422,
        numeroLiquidacion: '000000422',
        fromDate: '2025-05-01T04:00:00.000Z',
        toDate: '2025-05-31T04:00:00.000Z',
        statusId: 2,
        customerId: 66,
        cntr: 15,
        plts: 450,
        nave: 'MSC SANTA MARIA',
        agenciamiento: 273000,
        transporte: 0,
        osVenta: 0,
        osTraspaso: 0,
        total: 273000,
        estado: 'Liquidado',
        cliente: 'ACOGRAPES',
        tipo: 'Liquidación',
      },
    ],
  })
  items!: ILiquidacionAgencia[];

  /**
   * Total de registros encontrados (sin paginación).
   */
  @ApiProperty({
    description: 'Total de liquidaciones de agencia encontradas',
  })
  total!: number;

  /**
   * Página actual de la respuesta.
   */
  @ApiProperty({
    description: 'Página actual de la respuesta',
  })
  page!: number;

  /**
   * Cantidad de registros por página.
   */
  @ApiProperty({
    description: 'Cantidad de registros por página',
  })
  limit!: number;

  /**
   * Resumen de totales para la página actual.
   */
  @ApiProperty({
    description: 'Resumen de totales para los items de la página actual',
    example: {
      totalAgenciamiento: 125000,
      totalTransporte: 25000,
      totalOSVenta: 15000,
      totalOSTraspaso: 5000,
      totalGeneral: 170000,
      totalCNTR: 45,
      totalPLTS: 1250,
    },
  })
  resumen?: {
    totalAgenciamiento: number;
    totalTransporte: number;
    totalGate: number;
    totalOSVenta: number;
    totalVentaNeto: number;
    totalNeto: number;
    totalOSTraspaso: number;
    totalGeneral: number;
    totalCNTR: number;
    totalPLTS: number;
  };

  /**
   * ✅ REC #10: Información de temporada aplicada automáticamente.
   * Solo se incluye cuando se aplica una temporada automáticamente
   * o cuando se usa el filtro de temporada sin fechas específicas.
   * Ayuda al usuario a entender qué rango de datos está viendo.
   */
  @ApiProperty({
    description:
      'Información de temporada aplicada (solo presente si se aplicó temporada automática o por filtro)',
    required: false,
    example: {
      automatica: true,
      mensaje: 'Se aplicó la temporada activa automáticamente',
      fechaInicio: '2024-11-01',
      fechaFin: '2025-10-31',
    },
  })
  temporadaAplicada?: {
    automatica: boolean;
    mensaje: string;
    fechaInicio?: string;
    fechaFin?: string;
  };
}

/**
 * DTO para la respuesta del endpoint de datos del PDF de liquidación de agencia.
 * Incluye todos los datos necesarios para generar el PDF en el frontend.
 *
 * Uso: Respuesta del endpoint GET /api/v1/liquidaciones-agencia/:id/pdf-data.
 */
export class LiquidacionAgenciaPdfDataResponseDto {
  /**
   * Datos completos para generar el PDF de liquidación de agencia.
   */
  @ApiProperty({
    type: Object,
    description:
      'Datos completos para generar el PDF de liquidación de agencia',
  })
  data!: ILiquidacionAgenciaPdfData;

  /**
   * Mensaje de éxito.
   */
  @ApiProperty({
    example: 'Datos del PDF de liquidación agencia obtenidos exitosamente',
    description: 'Mensaje de confirmación del proceso',
  })
  message!: string;

  /**
   * Metadatos adicionales del PDF.
   */
  @ApiProperty({
    description: 'Metadatos adicionales para la generación del PDF',
    example: {
      version: '1.0',
      fechaGeneracion: '2024-11-15T10:30:00Z',
      tipoReporte: 'liquidacion-agencia',
    },
  })
  metadata?: {
    version: string;
    fechaGeneracion: Date;
    tipoReporte: string;
  };
}
